/**
 * The shared stylesheet every `@4f/ui` primitive registers before its own —
 * one home for the keyframes and the focus ring, injected exactly once.
 *
 * It exists because the alternative was measurably worse: eight components each
 * declared their own near-identical `@keyframes` (`f4-fade-in` in `Modal`,
 * `f4-lightbox-fade-in` in `Lightbox`, `f4-media-tile-shimmer` in `MediaTile`,
 * …), so a motion-token change updated none of them and three copies of the
 * same animation shipped in the bundle. Worse, `Drawer` *used* `f4-fade-in`
 * without declaring it — its scrim only faded on pages that happened to render
 * a `Modal` first. Twelve hand-rolled `:focus-visible` rules had the same
 * problem in the accessibility dimension.
 *
 * Names are `f4-`-prefixed and self-contained on purpose: they must not collide
 * with, or silently depend on load order against, the unprefixed keyframes
 * `@4f/styles` emits for app-authored CSS.
 *
 * Durations/easings still come from `@4f/styles` custom properties, with a
 * literal fallback so a primitive rendered before the app stylesheet loads
 * degrades to a sane timing instead of no animation.
 */
import { ensureStyles } from './inject-styles.ts';

const BASE_STYLE_ID = 'f4-base';

const BASE_CSS = `
@keyframes f4-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes f4-fade-out { from { opacity: 1; } to { opacity: 0; } }
@keyframes f4-scale-in { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
@keyframes f4-slide-up { from { opacity: 0; transform: translateY(var(--space-2, 8px)); } to { opacity: 1; transform: translateY(0); } }
@keyframes f4-slide-in-left { from { transform: translateX(-100%); } to { transform: translateX(0); } }
@keyframes f4-slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
@keyframes f4-shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }
@keyframes f4-spin { to { transform: rotate(360deg); } }

/* The one focus ring. Per-component offset via --f4-focus-offset, never a
   second hand-rolled outline. */
.f4-focus-ring:focus-visible {
  outline: 2px solid var(--color-focus);
  outline-offset: var(--f4-focus-offset, 2px);
}
.f4-focus-ring:focus:not(:focus-visible) { outline: none; }

/* The one loading shimmer — themed, and inert under reduced motion. */
.f4-skeleton {
  background-image: linear-gradient(
    90deg,
    var(--color-surface-sunken) 25%,
    var(--color-surface-raised) 50%,
    var(--color-surface-sunken) 75%
  );
  background-size: 200% 100%;
  animation: f4-shimmer var(--duration-loop, 1400ms) var(--ease-standard, ease) infinite;
}

@media (prefers-reduced-motion: reduce) {
  .f4-skeleton {
    animation: none;
    background-image: none;
    background-color: var(--color-surface-sunken);
  }
}
`;

/** Register the shared keyframes + focus ring. Idempotent; SSR-safe. */
export function ensureBaseStyles(): void {
  ensureStyles(BASE_STYLE_ID, BASE_CSS);
}
